import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { BRANDS as BRAND_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

// Desser is dark, ornate and dandy-elegant — deep cocoa with gold accents and
// the signature top-hat wordmark. This bespoke hero replaces BrandHero on the
// Desser page only, mirroring the editorial treatment given to Aperina and
// White Elephant.
// -----------------------------------------------------------------------------
const COCOA = "#1A0808";
const GOLD = "#E8B14B";
const CREAM = "#F7E2BB";
const RUBY = "#8A2E2E";

export default function DesserHero({ brand, inquiryHref }) {
  const { t } = useT();
  const navigate = useNavigate();

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: COCOA, color: CREAM }}
    >
      {/* Ambient background emblem — the "desser" wordmark scaled large, gold-tinted */}
      <img
        aria-hidden
        src={brand.logo}
        alt=""
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] max-w-none opacity-[0.10] invert brightness-150"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-20 pb-24 lg:pb-32">
        <button
          onClick={() => navigate("/#brands")}
          data-testid={BRAND_IDS.backHome}
          className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-100 transition-opacity mb-10 opacity-60"
          style={{ color: CREAM }}
        >
          <ArrowLeft className="w-4 h-4" /> {t("brandPage.backHome")}
        </button>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — editorial narrative */}
          <div className="lg:col-span-6">
            <p
              className="text-[0.7rem] md:text-xs font-bold uppercase mb-6"
              style={{ color: GOLD, letterSpacing: "0.36em" }}
            >
              {t(`brandContent.${brand.slug}.category`)}
            </p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-editorial italic leading-[1.02] text-[clamp(2.75rem,6vw,5.75rem)]"
              style={{ color: CREAM }}
            >
              &ldquo;{t(`brandContent.${brand.slug}.tagline`)}&rdquo;
            </motion.h1>

            {/* Gold script sub-title */}
            <p
              className="font-editorial italic mt-4 text-xl md:text-2xl"
              style={{ color: GOLD }}
            >
              {t("brandContent.desser.heroSub") ||
                "Six signatures. Heavenly crafted."}
            </p>

            <p
              className="mt-10 max-w-lg text-base md:text-lg leading-[1.75]"
              style={{ color: `${CREAM}dd` }}
            >
              {brand.story &&
                (t(`brandContent.${brand.slug}.story`) || brand.story)}
            </p>

            {/* Three-token attributes strip */}
            <p
              className="mt-8 text-xs font-bold uppercase"
              style={{ color: CREAM, letterSpacing: "0.3em" }}
            >
              {t("brandContent.desser.attr1") || "Chef-grade"}
              <span className="mx-2" style={{ color: GOLD }}>●</span>
              {t("brandContent.desser.attr2") || "Full-bodied"}
              <span className="mx-2" style={{ color: GOLD }}>●</span>
              {t("brandContent.desser.attr3") || "Pump system"}
            </p>

            <Link
              to={inquiryHref}
              data-testid={BRAND_IDS.ctaInquiry}
              className="mt-12 inline-flex items-center gap-2 rounded-full pl-6 pr-2 py-2 text-sm font-medium transition-transform hover:scale-[1.02] shadow-sm"
              style={{ background: GOLD, color: COCOA }}
            >
              {t("brandPage.partnerCta")}
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-full"
                style={{ background: COCOA, color: GOLD }}
              >
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          </div>

          {/* RIGHT — hero bottle (Framboise Madagascar crimson) */}
          <div className="lg:col-span-6 relative flex flex-col items-center">
            <motion.img
              initial={{ opacity: 0, y: 30, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              src={brand.frontImage || brand.image}
              alt="Desser bottle"
              data-testid={BRAND_IDS.deckImage(brand.slug, 0)}
              className="w-full max-w-[460px] mx-auto drop-shadow-[0_35px_60px_rgba(232,177,75,0.25)]"
            />

            {/* Golden thin divider */}
            <div
              className="mt-6 h-px w-24"
              style={{
                background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
              }}
            />

            {/* Under-bottle SKU line */}
            <p
              className="mt-4 text-xs font-bold uppercase"
              style={{ color: CREAM, letterSpacing: "0.3em" }}
            >
              {t("brandContent.desser.skuCount") || "6 Recipes"}
              <span className="mx-2" style={{ color: RUBY }}>●</span>
              {t("brandContent.desser.skuFormat") || "PET Bottle"}
              <span className="mx-2" style={{ color: RUBY }}>●</span>
              {t("brandContent.desser.skuUse") || "Coffee · Cocktail · Pastry"}
            </p>
          </div>
        </div>
      </div>

      {/* Fine bottom divider — signature gold hairline */}
      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
